import React, { useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule, ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { TPrix } from '../../../utils/types/tables/TPrix';

ModuleRegistry.registerModules([AllCommunityModule]);

interface TPrixGridProps {
    prix: TPrix[];
    isLoading?: boolean;
}

const TPrixGrid: React.FC<TPrixGridProps> = ({ prix, isLoading }) => {
    const gridRef = useRef<AgGridReact>(null);

    useEffect(() => {
        if (!gridRef.current?.api) return;
        if (isLoading) {
            gridRef.current.api.showLoadingOverlay();
        } else if (!prix.length) {
            gridRef.current.api.showNoRowsOverlay();
        } else {
            gridRef.current.api.hideOverlay();
        }
    }, [isLoading, prix]);

    const columnDefs: ColDef<TPrix>[] = [
        {
            field: 'nomListe',
            headerName: 'Liste',
            sortable: true,
            filter: true,
            pinned: 'left',
        },
        {
            field: 'nomArticle',
            headerName: 'Article',
            sortable: true,
            filter: true,
        },
        {
            field: 'nomBranche',
            headerName: 'Zone géographique',
            sortable: true,
            filter: true,
        },
        {
            field: 'type',
            headerName: 'Type',
            sortable: true,
            filter: true,
        },
        {
            field: 'montant',
            headerName: 'Montant',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return params.value.toFixed(2).replace('.', ',');
            },
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'nomDevise',
            headerName: 'Devise',
            sortable: true,
            filter: true,
        },
    ];

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const handleGridReady = (params: any) => {
        params.api.autoSizeAllColumns();
        if (isLoading) {
            params.api.showLoadingOverlay();
        }
    };

    return (
        <div style={{ height: '100vh', width: '100%' }}>
            <AgGridReact
                ref={gridRef}
                theme={myTheme}
                getRowId={(params) => params.data.id?.toString() ?? ''}
                rowData={isLoading ? [] : prix}
                columnDefs={columnDefs}
                pagination
                domLayout="normal"
                defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }}
                onGridReady={handleGridReady}
                enableCellTextSelection
                ensureDomOrder
                animateRows
                loadingOverlayComponent={() => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                            style={{
                                width: 20,
                                height: 20,
                                border: '3px solid #ccc',
                                borderTop: '3px solid #2196f3',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite',
                            }}
                        />
                        <span>Chargement...</span>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                )}
                overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
            />
        </div>
    );
};

export default TPrixGrid;
